import ApolloClient from '@/packages/apollo-client'
import Logger from '@/packages/logger'

function shouldCaptureApolloError (error) {
	return !(
		error?.name === 'ApolloError' ||
		error?.graphQLErrors ||
		error?.networkError ||
		error?.clientErrors ||
		error?.protocolErrors
	)
}

/**
 * ============================================================================
 * CORE APOLLO UTILITIES
 * Generic query/mutation execution with optional loading state management
 * ============================================================================
 */

/**
 * Validate GraphQL response for errors
 * @param {Object} data - Response data
 * @param {Array} errors - Response errors
 * @returns {Object} Validation result
 */
export function validateResponse (data, errors) {
	const responseErrors = errors?.length ? errors : data?.errors?.length ? data.errors : null
	if (responseErrors) {
		return { valid: false, error: responseErrors }
	}
	return { valid: true, data }
}

/**
 * Execute a simple query without loading state
 * @param {Object} options
 * @param {Object} options.query - GraphQL query
 * @param {Object} options.variables - Query variables
 * @param {string} options.fetchPolicy - Cache fetch policy
 * @returns {Promise<any>}
 */
export async function executeSimpleQuery ({ query, variables = {}, fetchPolicy = 'network-only' }) {
	try {
		const { data, errors } = await ApolloClient.query({
			query,
			variables,
			fetchPolicy
		})

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			return Promise.reject(validation.error)
		}

		return data
	} catch (error) {
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		return Promise.reject(error)
	}
}

/**
 * Execute a simple mutation without loading state
 * @param {Object} options
 * @param {Object} options.mutation - GraphQL mutation
 * @param {Object} options.variables - Mutation variables
 * @returns {Promise<any>}
 */
export async function executeSimpleMutation ({ mutation, variables = {} }) {
	try {
		const { data, errors } = await ApolloClient.mutate({
			mutation,
			variables
		})

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			return Promise.reject(validation.error)
		}

		return data
	} catch (error) {
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		return Promise.reject(error)
	}
}

/**
 * Execute a query with callbacks
 * @param {Object} options
 * @param {Object} options.query - GraphQL query
 * @param {Object} options.variables - Query variables
 * @param {string} options.fetchPolicy - Cache fetch policy
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Promise<any>}
 */
export async function executeQuery ({ query, variables = {}, fetchPolicy = 'network-only', onSuccess, onError }) {
	try {
		const { data, errors } = await ApolloClient.query({
			query,
			variables,
			fetchPolicy
		})

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			if (onError) onError(validation.error)
			return Promise.reject(validation.error)
		}

		if (onSuccess) {
			return onSuccess(data)
		}

		return data
	} catch (error) {
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		if (onError) onError(error)
		return Promise.reject(error)
	}
}

/**
 * Execute a mutation with callbacks
 * @param {Object} options
 * @param {Object} options.mutation - GraphQL mutation
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Promise<any>}
 */
export async function executeMutation ({ mutation, onSuccess, onError }) {
	try {
		const { data, errors } = await ApolloClient.mutate(mutation)

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			if (onError) onError(validation.error)
			return Promise.reject(validation.error)
		}

		if (onSuccess) {
			return onSuccess(data)
		}

		return data
	} catch (error) {
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		if (onError) onError(error)
		return Promise.reject(error)
	}
}

/**
 * ============================================================================
 * LOADING STATE UTILITIES
 * Query/mutation execution with Vuex loading state management
 * ============================================================================
 */

/**
 * Execute query/mutation with loading state management
 * @param {Object} options
 * @param {Function} options.commit - Vuex commit function
 * @param {string} options.mutationName - Loading mutation name
 * @param {string} options.loadingKey - Key for loading state
 * @param {Object} options.query - GraphQL query or mutation object
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {Function} options.onStart - Start callback (optional)
 * @returns {Promise<any>}
 */
export async function executeQueryWithLoading ({
	commit,
	mutationName,
	loadingKey,
	query,
	onSuccess,
	onError,
	onStart
}) {
	try {
		// Set loading state
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: true })
		}

		// Call onStart callback if provided
		if (onStart) onStart()

		// Determine if this is a query or mutation based on presence of 'mutation' key
		const result = query.mutation
			? await ApolloClient.mutate(query)
			: await ApolloClient.query(query)

		const { data, errors } = result

		// Clear loading state
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: false })
		}

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			if (onError) onError(validation.error)
			return Promise.reject(validation.error)
		}

		if (onSuccess) {
			return onSuccess(data)
		}

		return data
	} catch (error) {
		// Clear loading state on error
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: false })
		}
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		if (onError) onError(error)
		return Promise.reject(error)
	}
}

/**
 * Execute query with loading state and race condition prevention
 * @param {Object} options
 * @param {Function} options.commit - Vuex commit function
 * @param {string} options.mutationName - Loading mutation name
 * @param {string} options.loadingKey - Key for loading state
 * @param {Object} options.query - GraphQL query
 * @param {string} options.actionType - Action type for tracking
 * @param {number} options.requestId - Request ID for race condition check
 * @param {Function} options.onSuccess - Success callback
 * @param {boolean} options.skipRaceCheck - Skip race condition check
 * @returns {Promise<any>}
 */
export async function executeTrackedQuery ({
	commit,
	mutationName,
	loadingKey,
	query,
	actionType,
	requestId,
	onSuccess,
	skipRaceCheck = false
}) {
	// Dynamic import to avoid circular dependencies
	const { isCurrentRequest } = await import('./tracking')

	try {
		// Set loading state
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: true })
		}

		const { data, errors } = await ApolloClient.query(query)

		// Check if request is still current (prevent race conditions)
		if (!skipRaceCheck && requestId !== undefined && !isCurrentRequest(actionType, requestId)) {
			// Clear loading state for outdated request

			if (loadingKey && commit) {
				commit(mutationName, { [loadingKey]: false })
			}
			return // Silently ignore outdated requests
		}

		const validation = validateResponse(data, errors)
		if (!validation.valid) {
			throw new Error(JSON.stringify(validation.error))
		}

		const result = await onSuccess(data)

		// Clear loading state
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: false })
		}
		return result
	} catch (error) {
		// Clear loading state on error
		if (loadingKey && commit) {
			commit(mutationName, { [loadingKey]: false })
		}
		if (shouldCaptureApolloError(error)) {
			Logger.captureException(error)
		}
		return Promise.reject(error)
	}
}
